import { Source } from '../../constants/source';
import { ShareToolModal } from 'commerce-tools-ui-lib/public/components';
import { CREDIT_MEMOS_GATE, INVOICE_PREVIEW_GATE, AI_GENERATE_INVOICE_EMAIL_GATE } from '../../constants/gates';
import { useCallback, useMemo } from 'react';
import { getEmailSettingsConfig, getEmailShareTabInitialValues, getLinkOrPdfShareTabConfig, getSendEmailError } from '../../utils/invoiceEmail';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getFirstInvoiceAssociationWithRecordPage, getInvoiceEmailCommunicatorUrl, isCheckoutEnabled } from '../../utils/invoice';
import { getInvoiceEmailPreview, sendInvoiceEmail } from '../../api/InvoicesAPI';
import { usePaymentTrackingProperties } from '../../hooks/usePaymentTrackingProperties';
import { getInvoiceProperty } from '../../utils/property';
import { HS_ALLOWED_PAYMENT_METHODS } from '../../constants/properties';
import { useConnectedEmails } from 'commerce-tools-ui-lib/public/hooks';
import { useGetAppliedCreditMemos } from '../../hooks/useGetAppliedCreditMemos';
import { useGenerateInvoiceEmail } from '../../hooks/useGenerateInvoiceEmail';
import { hasContentChanged } from '../../utils/textComparison';
import { jsx as _jsx } from "react/jsx-runtime";
const InvoiceSendEmailModalV2 = ({
  invoice,
  onClose,
  isInitialInvoiceOpen,
  source,
  showCelebration = false,
  hasScope,
  hasGate = () => false,
  tracker,
  trackingLocation
}) => {
  const onCloseWithNoResponse = useCallback(() => onClose({}), [onClose]);
  const paymentTrackingProperties = usePaymentTrackingProperties();
  const contacts = invoice.associatedObjects.INVOICE_TO_CONTACT;
  const isUngatedForInvoicePreview = useMemo(() => hasGate(INVOICE_PREVIEW_GATE), [hasGate]);
  const isUngatedForCreditMemos = useMemo(() => hasGate(CREDIT_MEMOS_GATE), [hasGate]);
  const {
    appliedCreditMemos: appliedCreditMemosDetails
  } = useGetAppliedCreditMemos({
    invoiceId: invoice.objectId,
    skip: !isUngatedForCreditMemos
  });
  const appliedCreditMemos = useMemo(() => appliedCreditMemosDetails === null || appliedCreditMemosDetails === void 0 ? void 0 : appliedCreditMemosDetails.map(detail => ({
    creditMemoId: detail.creditMemoId,
    amountApplied: detail.amountApplied
  })), [appliedCreditMemosDetails]);
  const isUngatedForAiGenerateInvoiceEmail = useMemo(() => hasGate(AI_GENERATE_INVOICE_EMAIL_GATE), [hasGate]);
  const linkOrPdfShareTab = useMemo(() => getLinkOrPdfShareTabConfig(invoice, isUngatedForInvoicePreview, isUngatedForCreditMemos, appliedCreditMemos), [invoice, isUngatedForInvoicePreview, isUngatedForCreditMemos, appliedCreditMemos]);
  const {
    connectedEmailsLoading,
    connectedEmails
  } = useConnectedEmails();
  const {
    handleAiGenerateEmailContent,
    sendAiFeedback,
    lastGeneratedBodyRef
  } = useGenerateInvoiceEmail(invoice.objectId);
  const emailShareTabInitialValues = useMemo(() => {
    return getEmailShareTabInitialValues(contacts, source === Source.INVOICES_EDITOR && !!isInitialInvoiceOpen);
  }, [contacts, isInitialInvoiceOpen, source]);
  const emailCommunicatorUrl = useMemo(() => {
    const associatedObject = getFirstInvoiceAssociationWithRecordPage(invoice);
    if (associatedObject) {
      return getInvoiceEmailCommunicatorUrl(invoice, associatedObject);
    }
    return '';
  }, [invoice]);
  const getInvoiceEmailPreviewHtml = useCallback(({
    body
  }) => {
    if (!invoice.objectId) {
      throw new Error('invoice has no objectId');
    }
    return getInvoiceEmailPreview({
      invoiceId: invoice.objectId,
      body
    }).then(response => {
      return {
        html: response.messageAsHtml
      };
    });
  }, [invoice]);
  const sendEmail = useCallback((emailSendValues, onSuccess, onError) => {
    // Check if user modified AI-generated content and send feedback
    const originalGeneratedContent = lastGeneratedBodyRef.current;
    if (originalGeneratedContent && hasContentChanged(originalGeneratedContent, emailSendValues.body)) {
      void sendAiFeedback(emailSendValues.body);
    }
    sendInvoiceEmail({
      invoice,
      from: {
        email: emailSendValues.fromValue.email,
        name: emailSendValues.fromValue.name || undefined
      },
      toContacts: [emailSendValues.toContact],
      ccContacts: emailSendValues.ccContacts,
      informRecipientsOfUpdate: false,
      emailSubject: emailSendValues.subject,
      emailBody: emailSendValues.body
    }).then(() => {
      onSuccess();
      onClose({
        success: true
      });
    }).catch(error => {
      onError(getSendEmailError(error));
    });
  }, [invoice, onClose, sendAiFeedback, lastGeneratedBodyRef]);
  const trackerProps = useMemo(() => {
    return {
      tracker,
      trackingProperties: {
        objectType: 'INVOICE',
        location: trackingLocation,
        isEnrolled: Boolean(paymentTrackingProperties === null || paymentTrackingProperties === void 0 ? void 0 : paymentTrackingProperties.isEnrolled),
        paymentEnabled: isCheckoutEnabled(invoice),
        paymentMethods: getInvoiceProperty(invoice, HS_ALLOWED_PAYMENT_METHODS) || ''
      }
    };
  }, [tracker, trackingLocation, invoice, paymentTrackingProperties]);
  const emailShareTab = useMemo(() => {
    const emailSettingsConfig = getEmailSettingsConfig();
    const config = {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.components.sendInvoiceModal.emailShareTab.title"
      }),
      emailNotConnectedDescription: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.components.sendInvoiceModal.emailShareTab.emailNotConnectedDescription"
      }),
      initialValues: emailShareTabInitialValues,
      emailSettingsConfig,
      sendEmailButtonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.components.sendInvoiceModal.emailShareTab.sendButton"
      }),
      manualEmailButtonConfig: {
        emailCommunicatorUrl,
        buttonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.sendInvoiceModal.buttons.write.text_new"
        }),
        tooltipMainText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.sendInvoiceModal.buttons.write.tooltip.bodyText"
        })
      },
      loadEmailPreview: getInvoiceEmailPreviewHtml,
      sendEmail
    };
    return isUngatedForAiGenerateInvoiceEmail ? Object.assign({}, config, {
      aiGenerateEmailButtonConfig: {
        buttonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.sendInvoiceModal.emailShareTab.aiGenerateButton.label"
        }),
        tooltipMainText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.sendInvoiceModal.emailShareTab.aiGenerateButton.tooltip"
        }),
        generateSubjectAndBody: handleAiGenerateEmailContent
      }
    }) : config;
  }, [emailShareTabInitialValues, emailCommunicatorUrl, getInvoiceEmailPreviewHtml, sendEmail, isUngatedForAiGenerateInvoiceEmail, handleAiGenerateEmailContent]);
  return /*#__PURE__*/_jsx(ShareToolModal, {
    trackerProps: trackerProps,
    hasScope: hasScope,
    headerTitle: source === Source.INVOICES_EDITOR && !isInitialInvoiceOpen ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoices.components.sendInvoiceModal.headers.createdInvoice_new"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoices.components.sendInvoiceModal.headers.sendInvoice"
    }),
    defaultSelectedTab: connectedEmails.length > 0 ? 'emailShareTab' : 'linkOrPdfShareTab',
    isLoading: connectedEmailsLoading,
    showCelebration: showCelebration,
    onClose: onCloseWithNoResponse,
    tabs: {
      emailShareTab,
      linkOrPdfShareTab
    }
  });
};
export default InvoiceSendEmailModalV2;